import { Modal } from '@fozito/common-ui';
import { Form } from 'antd';
import { cashierCtrl } from 'modules/POS/Cashier/cashier.controller';
import useListenRequestSync from 'modules/POS/Cashier/hooks/useListenRequestSync';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { ICustomerPaymentDetail, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import QRPaymentDetail from 'modules/POS/PaymentDetails/QRPaymentDetail';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { ICashierOtherPayment } from 'modules/POS/services/types/payment';
import shopService from 'modules/shop/services';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
const FormStyled = styled(Form)`
height: 100%;
`;
export type ICashierQRPaymentMethod = (billId: string, payment: ICashierOtherPayment) => Promise<boolean>;
type Props = {
  onPayment: ICashierQRPaymentMethod;
  onCancel: () => void;
};
type Ref = {
  open: (billId: string, paymentType: string) => void;
};
export const useModalQRPaymentRef = () => useRef<Ref>(null);
const ModalQRPayment = forwardRef<Ref, Props>(({ onPayment, onCancel }, ref) => {
  const [form] = Form.useForm();
  const [billId, setOpen] = useState<string>('');
  const payments = shopService.selectors.paymentOthers();
  const paymentTypeRef = useRef<string>('');
  const socketContext = useSocketContext();
  const onClose = () => {
    socketContext.backToCheckIn();
    setOpen('');
  };
  const handleFinish = async (values: any) => {
    const data = values as { amount: number, detail: IBillInfoResData, paymentType: string };
    if (!billId) return;
    const payment: ICashierOtherPayment = {
      amount: data.amount,
      paymentType: data.paymentType,
      otherMethod: data.paymentType,
    };
    try {
      const isSuccess = await onPayment(billId, payment);
      if (!isSuccess) return;
      setOpen('');
    } catch (error) {
    } finally {
    }
  };
  const fetchDetail = async (billId: string, paymentType: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(billId);
      const detail = res.data.data;
      if (detail) {
        cashierCtrl.switchTicketWithDetailData(res.data.data);
        // TODO: SOCKET
        const items = detail?.items?.map((o) => ({
          name: o.itemName,
          price: o.price,
          quantity: o.quantity || 0,
          discount: o.discount || 0,
          requiredMember: o.requiredMember,
        })) || [];

        const amountFinal = detail.total || 0;
        socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
          billId,
          actor: TypePaymentActors.CASHIER,
          action: TypePaymentPassData.PASS_DATA,
          data: {
            cash: amountFinal,
            remaining: 0,
            chanceCash: 0,
            loyalty: 0,
            giftCard: 0,
            checkOthers: 0,
            card: 0,
            multiPay: 0,
            totalValue: amountFinal,
            subTotal: detail.subTotal || 0,
            cardFee: 0,
            saleTax: detail.saleTax || 0,
            usedTax: detail.useTax || 0,
            discount: (detail.discount || 0) + (detail.discountItem || 0),
          } as ICustomerPaymentDetail,
        });
        form.setFieldsValue({
          detail,
          items,
          amount: detail.total,
          paymentType,
        });
        return detail;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  useListenRequestSync(async (_billId) => {
    if (!billId) return;
    await fetchDetail(_billId, paymentTypeRef.current || '');
  });

  const onOpen: Ref['open'] = async (billId, paymentType) => {
    socketContext.switchCusPaymentQR(billId, paymentType);
    paymentTypeRef.current = paymentType;
    try {
      const detail = await fetchDetail(billId, paymentType);
      if (detail) {
        setOpen(billId);
      }
    } catch (error) {

    }
  };
  const _onCancel = () => {
    onClose();
    onCancel();
  };
  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  return (
    <Modal title='Card' open={!!billId} onClose={onClose} width={'100vw'} styles={{
      content: { height: '100vh', boxShadow: 'none' },
      header: { display: 'none' },
      body: { height: '100vh', maxHeight: 'unset' },
      wrapper: { background: '#fff' },
      mask: { display: 'none' }
    }}>
      <FormStyled form={form} onFinish={handleFinish}>
        <Form.Item name={'detail'} noStyle />
        <Form.Item name={'amount'} noStyle />
        <Form.Item name={'paymentType'} noStyle />
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const detail = getFieldValue('detail');
            const items = getFieldValue('items');
            const paymentType = getFieldValue('paymentType');
            const activePayment = payments.find(o => o.methodType === paymentType);
            return (
              <QRPaymentDetail
                date={detail?.createdDated || ''}
                customerName={detail?.customerName || 'Unknown Customer'}
                customerPhone={detail?.customerPhone}
                ticketNumber={detail?.ticketNumber || 0}
                items={items}
                subtotal={detail?.subTotal || 0}
                cardFee={detail?.cardFee || 0}
                saleTax={detail?.saleTax || 0}
                useTax={detail?.useTax || 0}
                discount={detail?.discount || 0}
                total={detail?.total || 0}
                onCancel={_onCancel}
                onConfirm={() => form.submit()}
                qrCodeImg={activePayment?.qrCode}
                QRName={activePayment?.methodName}
              />
            );
          }}
        </Form.Item>
      </FormStyled>
    </Modal>
  );
});
ModalQRPayment.displayName = 'ModalQRPayment';
export default ModalQRPayment;